import React, { useState, useEffect } from 'react';
import { ApiClient } from '../../api/client';
import type { PageItem } from '../../types';
import {
  ShieldCheck, CheckSquare, Square, FileText,
  Paperclip, Save, X, RefreshCw, ChevronRight, ChevronDown, CornerDownRight
} from 'lucide-react';
import { PAGE_LEVEL, ruleKey } from '../../utils/dpoRules';

// Egy szabály: (oldal, '*') = oldal szintű alapértelmezés; (oldal, pontszám) = alpont szintű felülírás.
interface ComplianceRuleItem {
  page_id: number;
  pont: string;
  is_required_data: number;
  is_required_attachment: number;
  legal_notes?: string;
}

interface SubItem { pont: string; title: string; count: number }

interface ComplianceRulesEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: number;
  tenantName?: string;
  pages: PageItem[];
  onSaved?: () => void;
}

const naturalPont = (a: string, b: string) => a.localeCompare(b, 'hu', { numeric: true, sensitivity: 'base' });

export const ComplianceRulesEditorModal: React.FC<ComplianceRulesEditorModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  tenantName,
  pages,
  onSaved
}) => {
  const [rules, setRules] = useState<Record<string, ComplianceRuleItem>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState<number | 'all'>('all');
  // Lenyitott oldalak és az alpontjaik (első lenyitáskor töltődnek be)
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [subItems, setSubItems] = useState<Record<number, SubItem[] | 'loading'>>({});

  useEffect(() => {
    if (!isOpen || !tenantId) return;
    const fetchRules = async () => {
      setLoading(true);
      try {
        const res = await ApiClient.getComplianceRules(tenantId);
        if (res.ok && res.data) {
          const map: Record<string, ComplianceRuleItem> = {};
          res.data.forEach((r: any) => {
            if (!r.page_id) return;
            const pont = String(r.pont || PAGE_LEVEL).trim() || PAGE_LEVEL;
            map[ruleKey(r.page_id, pont)] = {
              page_id: r.page_id,
              pont,
              is_required_data: r.is_required_data ? 1 : 0,
              is_required_attachment: r.is_required_attachment ? 1 : 0,
              legal_notes: r.legal_notes || '',
            };
          });
          setRules(map);
        }
      } catch (e) {
        console.error('Hiba a megfelelőségi szabályok betöltésekor:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchRules();
  }, [isOpen, tenantId]);

  if (!isOpen) return null;

  const pageRule = (pageId: number): ComplianceRuleItem =>
    rules[ruleKey(pageId, PAGE_LEVEL)] || { page_id: pageId, pont: PAGE_LEVEL, is_required_data: 0, is_required_attachment: 0, legal_notes: '' };

  const updateRule = (pageId: number, pont: string, patch: Partial<ComplianceRuleItem>) => {
    setRules(prev => {
      const k = ruleKey(pageId, pont);
      const base = prev[k] || (pont === PAGE_LEVEL
        ? { page_id: pageId, pont, is_required_data: 0, is_required_attachment: 0, legal_notes: '' }
        // új alpont-felülírás: az oldal aktuális értékéből indul
        : { ...pageRule(pageId), pont, legal_notes: '' });
      return { ...prev, [k]: { ...base, ...patch } };
    });
  };

  const handleToggleRule = (pageId: number, pont: string, field: 'is_required_data' | 'is_required_attachment') => {
    const k = ruleKey(pageId, pont);
    const current = rules[k] || (pont === PAGE_LEVEL ? pageRule(pageId) : { ...pageRule(pageId), pont });
    updateRule(pageId, pont, { [field]: current[field] === 1 ? 0 : 1 });
  };

  const handleNoteChange = (pageId: number, pont: string, note: string) => updateRule(pageId, pont, { legal_notes: note });

  // Alpont egyedi beállítása be/ki (ki = az oldal szabályát örökli)
  const toggleOverride = (pageId: number, pont: string) => {
    const k = ruleKey(pageId, pont);
    setRules(prev => {
      if (prev[k]) {
        const { [k]: _removed, ...rest } = prev;
        void _removed;
        return rest;
      }
      return { ...prev, [k]: { ...pageRule(pageId), pont, legal_notes: '' } };
    });
  };

  const loadSubItems = async (pageId: number) => {
    setSubItems(prev => ({ ...prev, [pageId]: 'loading' }));
    try {
      const res: any = await ApiClient.getPageRecords(pageId, undefined, 'all', false, 2000);
      const rows: any[] = res?.data || [];
      const byPont = new Map<string, SubItem>();
      for (const r of rows) {
        let d: any = r.parsedData;
        if (!d) { try { d = JSON.parse(r.data || '{}'); } catch { d = {}; } }
        const pont = String(d.pont ?? '').trim();
        if (!pont) continue;
        const it = byPont.get(pont) || { pont, title: String(d.cim || d.megnevezes || d.nev || ''), count: 0 };
        it.count++;
        byPont.set(pont, it);
      }
      setSubItems(prev => ({ ...prev, [pageId]: [...byPont.values()].sort((a, b) => naturalPont(a.pont, b.pont)) }));
    } catch (e) {
      console.error('Hiba az alpontok betöltésekor:', e);
      setSubItems(prev => ({ ...prev, [pageId]: [] }));
    }
  };

  const toggleExpanded = (pageId: number) => {
    setExpanded(prev => ({ ...prev, [pageId]: !prev[pageId] }));
    if (!subItems[pageId]) loadSubItems(pageId);
  };

  const overrideCount = (pageId: number) =>
    Object.values(rules).filter(r => r.page_id === pageId && r.pont !== PAGE_LEVEL).length;

  const handleSave = async () => {
    setSaving(true);
    setSavedSuccess(false);
    try {
      // A teljes szabálykészlet cseréje: a kivett alpont-felülírások is törlődnek
      const rulesList = Object.values(rules);
      const res = await ApiClient.saveComplianceRules(tenantId, rulesList, true);
      if (res.ok) {
        setSavedSuccess(true);
        setTimeout(() => setSavedSuccess(false), 3000);
        if (onSaved) onSaved();
      } else {
        alert(res.error || 'Hiba a szabályok mentésekor');
      }
    } catch (e: any) {
      alert('Hiba: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const getPageChapter = (p: PageItem): number => {
    if (p.chapter) return p.chapter;
    if (p.slug.startsWith('1-') || p.slug.startsWith('szerv')) return 1;
    if (p.slug.startsWith('2-') || p.slug.startsWith('tev')) return 2;
    if (p.slug.startsWith('3-') || p.slug.startsWith('gazd')) return 3;
    return 1;
  };

  // Group pages by chapter
  const filteredPages = pages.filter(p => {
    if (selectedChapter === 'all') return true;
    return getPageChapter(p) === selectedChapter;
  });

  const ToggleBtn: React.FC<{ on: boolean; kind: 'data' | 'att'; onClick: () => void; small?: boolean }> = ({ on, kind, onClick, small }) => (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 ${small ? 'px-2 py-1 text-[11px]' : 'px-3 py-1.5 text-xs'} rounded-lg font-medium border transition cursor-pointer ${
        on
          ? kind === 'data'
            ? 'bg-blue-50 dark:bg-blue-950/50 border-blue-400 text-blue-700 dark:text-blue-300 shadow-2xs'
            : 'bg-amber-50 dark:bg-amber-950/50 border-amber-400 text-amber-700 dark:text-amber-300 shadow-2xs'
          : 'bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400'
      }`}
      title={kind === 'data' ? 'Kötelező adatbevitel kijelölése' : 'Kötelező fájlmelléklet feltöltés kijelölése'}
    >
      {on
        ? <CheckSquare className={`w-3.5 h-3.5 ${kind === 'data' ? 'text-blue-600' : 'text-amber-600'}`} />
        : <Square className="w-3.5 h-3.5" />}
      <span>{kind === 'data' ? 'Adat kötelező' : 'Melléklet kötelező'}</span>
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-150">
      <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-4xl w-full h-[85vh] flex flex-col shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        {/* Fejléc */}
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between shrink-0 bg-slate-50/50 dark:bg-slate-950/50">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-500/10 text-indigo-600 dark:text-cyan-400 rounded-xl border border-indigo-500/20">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <span>DPO / Szakjogászi Kötelezettség-kijelölő</span>
                {tenantName && (
                  <span className="text-xs px-2.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-normal">
                    {tenantName}
                  </span>
                )}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Jelölje ki azokat a fejezeteket és alpontokat, ahol kötelező adatot felvinni és/vagy kötelező mellékletet csatolni
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tájékoztató sáv és fejezetszűrő */}
        <div className="p-4 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-1.5 overflow-x-auto text-xs">
            <span className="text-slate-400 text-[11px] font-medium mr-1">Fejezet szűrése:</span>
            <button
              onClick={() => setSelectedChapter('all')}
              className={`px-3 py-1.5 rounded-lg font-medium transition cursor-pointer ${
                selectedChapter === 'all'
                  ? 'bg-indigo-600 text-white shadow-xs'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
              }`}
            >
              Összes fejezet
            </button>
            <button
              onClick={() => setSelectedChapter(1)}
              className={`px-3 py-1.5 rounded-lg font-medium transition cursor-pointer ${
                selectedChapter === 1
                  ? 'bg-indigo-600 text-white shadow-xs'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
              }`}
            >
              I. Szervezeti adatok
            </button>
            <button
              onClick={() => setSelectedChapter(2)}
              className={`px-3 py-1.5 rounded-lg font-medium transition cursor-pointer ${
                selectedChapter === 2
                  ? 'bg-indigo-600 text-white shadow-xs'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
              }`}
            >
              II. Működés és tevékenység
            </button>
            <button
              onClick={() => setSelectedChapter(3)}
              className={`px-3 py-1.5 rounded-lg font-medium transition cursor-pointer ${
                selectedChapter === 3
                  ? 'bg-indigo-600 text-white shadow-xs'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
              }`}
            >
              III. Gazdálkodási adatok
            </button>
          </div>

          <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
            <span className="flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5 text-blue-500" />
              <span>Adat kötelező</span>
            </span>
            <span className="flex items-center gap-1.5">
              <Paperclip className="w-3.5 h-3.5 text-amber-500" />
              <span>Melléklet kötelező</span>
            </span>
          </div>
        </div>

        {/* Alpontok táblázata */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-12 text-slate-400 text-xs">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-indigo-500" />
              <span>Szabályok betöltése...</span>
            </div>
          ) : filteredPages.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-xs">
              Nincsenek megjeleníthető alpontok.
            </div>
          ) : (
            <div className="space-y-2">
              {filteredPages.map((p) => {
                const rule = pageRule(p.id);
                const isOpenRow = Boolean(expanded[p.id]);
                const subs = subItems[p.id];
                const nOverride = overrideCount(p.id);

                return (
                  <div
                    key={p.id}
                    className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xs hover:border-slate-300 dark:hover:border-slate-700 transition"
                  >
                    <div className="p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => toggleExpanded(p.id)}
                            className="p-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 cursor-pointer"
                            title={isOpenRow ? 'Alpontok elrejtése' : 'Alpontok megjelenítése és egyedi beállítása'}
                            aria-expanded={isOpenRow}
                          >
                            {isOpenRow ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                          <span className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                            {p.code || (p.chapter && p.order_index ? `${p.chapter}.${p.order_index}` : p.slug)}
                          </span>
                          <h4 className="text-xs font-bold text-slate-900 dark:text-white truncate">
                            {p.title}
                          </h4>
                          {nOverride > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-cyan-300 border border-indigo-200 dark:border-indigo-800 shrink-0">
                              {nOverride} egyedi alpont
                            </span>
                          )}
                        </div>
                        <input
                          type="text"
                          placeholder="Megjegyzés / jogi indoklás (pl. Nem releváns, vagy Szabályzat szükséges)..."
                          value={rule.legal_notes || ''}
                          onChange={(e) => handleNoteChange(p.id, PAGE_LEVEL, e.target.value)}
                          className="w-full text-[11px] px-2.5 py-1 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>

                      <div className="flex items-center gap-3 shrink-0 self-end sm:self-center">
                        <ToggleBtn on={rule.is_required_data === 1} kind="data" onClick={() => handleToggleRule(p.id, PAGE_LEVEL, 'is_required_data')} />
                        <ToggleBtn on={rule.is_required_attachment === 1} kind="att" onClick={() => handleToggleRule(p.id, PAGE_LEVEL, 'is_required_attachment')} />
                      </div>
                    </div>

                    {isOpenRow && (
                      <div className="border-t border-slate-100 dark:border-slate-800 px-3.5 py-2.5 bg-slate-50/60 dark:bg-slate-950/40 space-y-1.5">
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">
                          Az alpontok alapból a fenti oldalszintű beállítást követik. Az „Egyedi” kapcsolóval egy alpont saját beállítást kaphat.
                        </p>
                        {subs === 'loading' || subs === undefined ? (
                          <div className="text-[11px] text-slate-400 flex items-center gap-1.5 py-2">
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Alpontok betöltése...
                          </div>
                        ) : subs.length === 0 ? (
                          <div className="text-[11px] text-slate-400 py-2">Ezen az oldalon nincs pontszámmal ellátott bejegyzés.</div>
                        ) : (
                          subs.map((it) => {
                            const k = ruleKey(p.id, it.pont);
                            const own = rules[k];
                            const eff = own || rule;
                            return (
                              <div key={k} className="flex flex-col md:flex-row md:items-center gap-2 py-1.5 border-b last:border-b-0 border-slate-100 dark:border-slate-800/80">
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <CornerDownRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                                  <span className="font-mono text-[11px] font-bold px-1.5 py-0.5 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 shrink-0 max-w-[14rem] truncate" title={it.pont}>
                                    {it.pont}
                                  </span>
                                  <span className="text-[11px] text-slate-700 dark:text-slate-300 truncate" title={it.title}>{it.title || '–'}</span>
                                  {it.count > 1 && <span className="text-[10px] text-slate-400 shrink-0">({it.count} bejegyzés)</span>}
                                </div>
                                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                                  <label className="flex items-center gap-1 text-[11px] text-slate-600 dark:text-slate-400 cursor-pointer select-none" title="Ki: az oldal beállítását követi. Be: saját beállítás.">
                                    <input type="checkbox" checked={Boolean(own)} onChange={() => toggleOverride(p.id, it.pont)} className="accent-indigo-600" />
                                    Egyedi
                                  </label>
                                  {own ? (
                                    <>
                                      <ToggleBtn small on={own.is_required_data === 1} kind="data" onClick={() => handleToggleRule(p.id, it.pont, 'is_required_data')} />
                                      <ToggleBtn small on={own.is_required_attachment === 1} kind="att" onClick={() => handleToggleRule(p.id, it.pont, 'is_required_attachment')} />
                                    </>
                                  ) : (
                                    <span className="text-[11px] text-slate-400 italic">
                                      Oldal szerint: {[eff.is_required_data ? 'adat' : '', eff.is_required_attachment ? 'melléklet' : ''].filter(Boolean).join(' + ') || 'nem kötelező'}
                                    </span>
                                  )}
                                </div>
                                {own && (
                                  <input
                                    type="text"
                                    placeholder="Megjegyzés ehhez az alponthoz..."
                                    value={own.legal_notes || ''}
                                    onChange={(e) => handleNoteChange(p.id, it.pont, e.target.value)}
                                    className="md:w-56 text-[11px] px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                  />
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Lábjegyzet & Mentés sáv */}
        <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between shrink-0 bg-slate-50/50 dark:bg-slate-950/50">
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {savedSuccess && (
              <span className="text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1">
                ✓ A megfelelőségi szabályok sikeresen elmentve!
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            >
              Bezárás
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl text-xs font-semibold shadow-md transition cursor-pointer"
            >
              <Save className="w-4 h-4" />
              <span>{saving ? 'Mentés folyamatban...' : 'Szabályok érvényesítése'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
